import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import roomService from "../../services/room";
import service from "../../services/unit";
import queryDefault from "../../utils/queryDefault";

export default function UnitEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const [room, setRoom] = useState({ id: 0, name: "" });

  const schema = yup.object().shape({
    name: yup.string().min(2).required("Name is a required field and length > 2")
  });
  const form = useForm({
    defaultValues: {
      room_id: id,
      name: "",
      description: "",
      length: 0,
      width: 0,
      status: "Available",
      admin_name: user.username,
    },
    resolver: yupResolver(schema),
  });

  const updateUnit = useMutation(
    "update_unit",
    service.updateUnit,
    {
      onSuccess: () => {
        navigate(`/app/properties/property/room/detail/${room.id}`);

        notification.onOpen({
          message: "Unit was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    const submittedValues = { ...values, id };
    updateUnit.mutate(submittedValues);
  };

  const currentUnit = useQuery("current-unit", () =>
    service.getUnitById(Number(id))
  );

  useEffect(() => {
    if (currentUnit.isFetched) {
      form.setValue("room_id", get(currentUnit, "data.data.unit.room_id"));
      form.setValue("name", get(currentUnit, "data.data.unit.name"));
      form.setValue("description", get(currentUnit, "data.data.unit.description"));
      form.setValue("length", get(currentUnit, "data.data.unit.length"));
      form.setValue("width", get(currentUnit, "data.data.unit.width"));
      form.setValue("status", get(currentUnit, "data.data.unit.status"));
      roomService.getRoomById(get(currentUnit, "data.data.unit.room_id")).then(response => {
        setRoom(response?.data);
      })
    }
    // eslint-disable-next-line
  }, [currentUnit.isFetched]);

  return (
    <MainLayout>
      <Breadcrumb
        label="Create Unit"
        breadcrumbs={[
          { label: "Properties", href: "/app/properties/property" },
          { label: "Unit", href: `/app/properties/property/unit/detail/${id}` },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="property"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Description</FormLabel>
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="medium"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Length (in meter)</FormLabel>
                <Controller
                  name="length"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Width (in meter)</FormLabel>
                <Controller
                  name="width"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Status</FormLabel>
                <Controller
                  name="status"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={"Available"} key={0}>Available</MenuItem>
                        <MenuItem value={"Booked"} key={1}>Booked</MenuItem>
                        <MenuItem value={"Rented"} key={2}>Rented</MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateUnit.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
